import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './ArticlesList.css';

// Import shared utilities and components
import { extractMetadata, formatHebrewDate } from '../../../utils/markdownUtils';
import useResponsive from '../../../utils/useResponsive';

// Constant for default image
const DEFAULT_IMAGE = '/images/default-article-thumb.jpg';

const ArticlesList = ({ folderName, basePath, defaultCategory }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isMobile } = useResponsive();

  useEffect(() => {
    const loadArticles = async () => {
      try {
        setLoading(true);

        // Try to use the PHP API first (works with files added directly to build folder)
        try {
          const apiResponse = await fetch(`/md-files.php?folder=${encodeURIComponent(folderName)}`);
          if (apiResponse.ok) {
            const data = await apiResponse.json();
            if (data.files && data.files.length > 0) {
              console.log(`Found ${data.files.length} MD files via PHP API for ${folderName}:`, data.files);
              await processApiFiles(data.files);
              return;
            }
          }
        } catch (apiError) {
          console.log('PHP API not available, trying webpack method:', apiError);
          loadArticlesFromWebpack();
          return;
        }

        // Fallback to webpack method if API fails
        loadArticlesFromWebpack();

      } catch (err) {
        console.error('Error loading articles:', err);
        // Fallback to webpack method if all else fails
        loadArticlesFromWebpack();
      }
    };

    // Process files from API endpoint
    const processApiFiles = async (files) => {
      try {
        const articlePromises = files.map(async (file) => {
          try {
            const mdResponse = await fetch(file.path);
            const content = await mdResponse.text();

            // Use shared metadata extraction utility
            const { metadata } = extractMetadata(content);

            // Generate a fallback title if none exists in metadata
            const fallbackTitle = file.name
              .replace('.md', '')
              .replace(/-/g, ' ')
              .replace(/\b\w/g, c => c.toUpperCase());

            return {
              slug: file.name.replace('.md', ''),
              title: metadata.title || metadata.כותרת || fallbackTitle,
              date: metadata.date || metadata.תאריך || new Date().toISOString(),
              description: metadata.description || metadata.תיאור || '',
              thumbnail: metadata.thumbnail || metadata.תמונה ? `/${metadata.thumbnail || metadata.תמונה}` : '/images/default-article-thumb.jpg',
              category: metadata.category || metadata.קטגוריות ? metadata.category || metadata.קטגוריות.split(',')[0].trim() : defaultCategory,
              author: metadata.author || metadata.מחבר || ''
            };
          } catch (err) {
            console.error(`Error loading ${file.name}:`, err);
            return null;
          }
        });

        const loadedArticles = (await Promise.all(articlePromises)).filter(Boolean);

        // Sort articles by date (newest first)
        const sortedArticles = loadedArticles
          .filter(article => article.title)
          .sort((a, b) => {
            // Try to parse dates, fallback to string comparison
            try {
              return new Date(b.date) - new Date(a.date);
            } catch (e) {
              return b.date.localeCompare(a.date);
            }
          });

        setArticles(sortedArticles);
        setLoading(false);
      } catch (err) {
        console.error('Error processing API files:', err);
        loadArticlesFromWebpack();
      }
    };

    const loadArticlesFromWebpack = async () => {
      try {
        // Fallback to the old webpack require.context method
        const context = require.context('../../../../public/md', true, /\.md$/);
        const fileNames = context.keys()
          .filter(key => key.includes(`/${folderName}/`));

        if (!fileNames.length) {
          setError('No articles found');
          setLoading(false);
          return;
        }

        const articlePromises = fileNames.map(async (fileName) => {
          const response = await fetch(`/md/${fileName.slice(2)}`);
          const content = await response.text();

          // Improved metadata extraction
          let metadata = {};
          const metadataMatch = content.match(/^---\s*\n([\s\S]*?)\n---/);

          if (metadataMatch) {
            const metadataContent = metadataMatch[1];
            metadata = metadataContent.split('\n').reduce((acc, line) => {
              const [key, ...values] = line.split(':');
              if (key && values.length) {
                acc[key.trim()] = values.join(':').trim();
              }
              return acc;
            }, {});
          }

          // Generate a fallback title if none exists in metadata
          const fallbackTitle = fileName
            .split('/')
            .pop()
            .replace('.md', '')
            .replace(/-/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase());

          // Validate image path before returning article object
          const imagePath = metadata.thumbnail || metadata.תמונה;
          // Be strict about what we consider a valid image path
          const isValidPath = imagePath &&
            typeof imagePath === 'string' &&
            imagePath.trim() !== '' &&
            !imagePath.includes('undefined') &&
            !imagePath.includes('null');

          return {
            slug: fileName.split('/').pop().replace('.md', ''),
            title: metadata.title || metadata.כותרת || fallbackTitle,
            date: metadata.date || metadata.תאריך || new Date().toISOString(),
            description: metadata.description || metadata.תיאור || '',
            thumbnail: isValidPath ? `/${imagePath}` : DEFAULT_IMAGE,
            category: metadata.category || metadata.קטגוריות ? metadata.category || metadata.קטגוריות.split(',')[0].trim() : defaultCategory,
            author: metadata.author || metadata.מחבר || ''
          };
        });

        const loadedArticles = await Promise.all(articlePromises);

        // Sort articles by date (newest first)
        const sortedArticles = loadedArticles
          .filter(article => article.title)
          .sort((a, b) => {
            // Try to parse dates, fallback to string comparison
            try {
              return new Date(b.date) - new Date(a.date);
            } catch (e) {
              return b.date.localeCompare(a.date);
            }
          });

        setArticles(sortedArticles);

      } catch (err) {
        console.error('Error loading articles from webpack context:', err);
        setError(err.message || 'Failed to load articles');
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, [folderName, basePath, defaultCategory]);

  if (loading) {
    return (
      <div className="articles-preview">
        <ul className="articles-grid">
          {[...Array(3)].map((_, index) => (
            <li className="article-card article-skeleton" key={`skeleton-${index}`} aria-hidden="true">
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (error) {
    return (
      <div className="articles-preview">
        <div className="error-container" role="alert" style={{ color: 'white', opacity: 0.8, textAlign: 'center', margin: '4rem 0' }}>
          <h3>{error}</h3>
          <p>Try refreshing the page or come back later.</p>
          <button
            className="cta"
            onClick={() => window.location.reload()}
            aria-label="Retry loading articles"
            style={{ background: 'transparent', border: '1px solid var(--accent)', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', marginTop: '1rem', color: 'var(--accent)' }}
          >
            RETRY SYSTEM
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`articles-preview ${isMobile ? 'mobile-view' : ''}`}>
      <ul className="articles-grid">
        {articles.map((article, index) => (
          <li className="article-card" key={article.slug || index}>
            <Link
              to={`/${basePath}/${article.slug}.md`}
              className="card-link"
              onClick={(e) => {
                if (isMobile) {
                  const target = e.currentTarget;
                  target.style.transform = 'scale(0.98)';
                  setTimeout(() => {
                    target.style.transform = '';
                  }, 150);
                }
              }}
            >
              <div className="card-media">
                <img
                  src={DEFAULT_IMAGE}
                  alt={article.title}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: (article.thumbnail && article.thumbnail !== DEFAULT_IMAGE && !article.thumbnail.includes('//')) ? 'none' : 'block'
                  }}
                />
                {article.thumbnail && article.thumbnail !== DEFAULT_IMAGE && !article.thumbnail.includes('//') && (
                  <img
                    src={article.thumbnail}
                    alt={article.title}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.parentNode.firstChild.style.display = 'block';
                      e.target.style.display = 'none';
                    }}
                  />
                )}
              </div>
              <div className="card-body">
                {article.category && (
                  <span className="card-tag">{article.category}</span>
                )}
                <h2 className="card-title">{article.title}</h2>
                {article.description && <p className="card-excerpt">{article.description}</p>}
              </div>
              <div className="card-meta">
                <time>{formatHebrewDate(article.date)}</time>
                <span className="cta">READ &gt;</span>
              </div>
            </Link>
          </li>
        ))}
      </ul>

      {/* Safari-only invisible block that only appears on mobile Safari - positioned at the bottom */}
      <div className="safari-mobile-block">
        {/* This block is invisible and only appears on mobile Safari browsers */}
      </div>
    </div>
  );
};

export default ArticlesList;